import type { RosterMember } from '../useHouseholdRoster';
import { textOnMemberColor } from '../../../theme/memberColor';

const SIZE_CLASS = {
  sm: 'h-5 w-5 text-[10px]',
  md: 'h-7 w-7 text-xs',
  lg: 'h-9 w-9 text-sm',
} as const;

/**
 * A member's colored initial circle (Component - MemberChip.dc.html). Always the member's
 * own profile hex, applied inline — colour is user data (WS-2's `color` field), not a
 * design token, so it can never be picked based on the member's name (usability testing,
 * 2026-08-01: named-member mapping meant renaming yourself changed nothing, and anyone not
 * named Faye/Dan/Ezry/Kora fell back to a different treatment entirely).
 */
export function MemberAvatar({
  member,
  size = 'md',
  className = '',
}: {
  member: RosterMember;
  size?: keyof typeof SIZE_CLASS;
  className?: string;
}): JSX.Element {
  const initial = (member.avatarEmoji ?? member.name)
    .slice(0, member.avatarEmoji ? 2 : 1)
    .toUpperCase();
  return (
    <span
      className={`flex flex-shrink-0 items-center justify-center rounded-full font-bold ${SIZE_CLASS[size]} ${className}`}
      style={{ backgroundColor: member.color, color: textOnMemberColor(member.color) }}
      title={member.name}
      data-testid="member-avatar"
      data-member-id={member.id}
    >
      {initial}
    </span>
  );
}

/** Dashed "?" ring for an unassigned item — "a question, not a blank" (Screen D flow notes). */
export function UnassignedAvatar({ size = 'md' }: { size?: keyof typeof SIZE_CLASS }): JSX.Element {
  return (
    <span
      className={`flex flex-shrink-0 items-center justify-center rounded-full border border-dashed border-line-strong bg-raised font-bold text-fg-3 ${SIZE_CLASS[size]}`}
      data-testid="member-avatar-unassigned"
    >
      ?
    </span>
  );
}
